// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, Divider } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";

// theme
import { B1Tiny, ModalActionsLoadingButton, ModalContentSwitch } from "../../../theme/styled";

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const NotificationsDefaults = {
  nNotificationLogs: false,
  nNotificationMails: false,
  nNotificationReportDaily: false,
  nNotificationReportWeekly: false,
  nNotificationReportMonthly: false,
  nNotificationReportEnd: false,
  nNotificationOpen: true,
  nNotificationClose: true,
  nNotificationProtection: false,
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function NotificationsRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get switch display values (On/Off)
  const getSwitchDisplay = (value) => (value === true ? "On" : "Off");

  return (
    <Stack spacing={0.5}>
      {renderParam("Notifications in MetaTrader experts tab", getSwitchDisplay(config.nNotificationLogs))}
      {renderParam("Notifications as email", getSwitchDisplay(config.nNotificationMails))}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Daily summary notifications", getSwitchDisplay(config.nNotificationReportDaily))}
      {renderParam("Weekly summary notifications", getSwitchDisplay(config.nNotificationReportWeekly))}
      {renderParam("Monthly summary notifications", getSwitchDisplay(config.nNotificationReportMonthly))}
      {renderParam("Notifications at programm stop", getSwitchDisplay(config.nNotificationReportEnd))}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Notifications for trade opening", getSwitchDisplay(config.nNotificationOpen))}
      {renderParam("Notifications for trade closure", getSwitchDisplay(config.nNotificationClose))}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Notifications for monitoring events", getSwitchDisplay(config.nNotificationProtection))}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const NotificationsConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  NotificationsConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const autoCloseTimeoutRef = useRef(null);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Apply all the default values to the form
    Object.keys(NotificationsDefaults).forEach((key) => {
      setFieldValue(key, NotificationsDefaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nNotificationLogs: initialData?.nNotificationLogs ?? NotificationsDefaults.nNotificationLogs,
      nNotificationMails: initialData?.nNotificationMails ?? NotificationsDefaults.nNotificationMails,
      nNotificationReportDaily: initialData?.nNotificationReportDaily ?? NotificationsDefaults.nNotificationReportDaily,
      nNotificationReportWeekly: initialData?.nNotificationReportWeekly ?? NotificationsDefaults.nNotificationReportWeekly,
      nNotificationReportMonthly: initialData?.nNotificationReportMonthly ?? NotificationsDefaults.nNotificationReportMonthly,
      nNotificationReportEnd: initialData?.nNotificationReportEnd ?? NotificationsDefaults.nNotificationReportEnd,
      nNotificationOpen: initialData?.nNotificationOpen ?? NotificationsDefaults.nNotificationOpen,
      nNotificationClose: initialData?.nNotificationClose ?? NotificationsDefaults.nNotificationClose,
      nNotificationProtection: initialData?.nNotificationProtection ?? NotificationsDefaults.nNotificationProtection,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } = formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

    // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }
  
  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure notifications
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure notifications</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationLogs */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter value="Notifications in MetaTrader experts tab" helpReference="HandleNotifications" helpText="" helpLink="" fieldName="nNotificationLogs" touched={touched} errors={errors} />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationLogs}
                        onChange={(event) => {
                          setFieldValue("nNotificationLogs", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationMails */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter value="Notifications as email" helpReference="HandleNotifications" helpText="" helpLink="" fieldName="nNotificationMails" touched={touched} errors={errors} />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationMails}
                        onChange={(event) => {
                          setFieldValue("nNotificationMails", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationReportDaily */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter value="Daily summary notifications" helpReference="HandleNotifications" helpText="" helpLink="" fieldName="nNotificationReportDaily" touched={touched} errors={errors} />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationReportDaily}
                        onChange={(event) => {
                          setFieldValue("nNotificationReportDaily", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationReportWeekly */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter value="Weekly summary notifications" helpReference="HandleNotifications" helpText="" helpLink="" fieldName="nNotificationReportWeekly" touched={touched} errors={errors} />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationReportWeekly}
                        onChange={(event) => {
                          setFieldValue("nNotificationReportWeekly", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationReportMonthly */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter value="Monthly summary notifications" helpReference="HandleNotifications" helpText="" helpLink="" fieldName="nNotificationReportMonthly" touched={touched} errors={errors} />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationReportMonthly}
                        onChange={(event) => {
                          setFieldValue("nNotificationReportMonthly", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationReportEnd */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter value="Notifications at programm stop" helpReference="HandleNotifications" helpText="" helpLink="" fieldName="nNotificationReportEnd" touched={touched} errors={errors} />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationReportEnd}
                        onChange={(event) => {
                          setFieldValue("nNotificationReportEnd", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationOpen */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Notifications for trade opening"
                        helpReference="HandleNotifications"
                        helpText=""
                        helpLink=""
                        fieldName="nNotificationOpen"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationOpen}
                        onChange={(event) => {
                          setFieldValue("nNotificationOpen", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationClose */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Notifications for trade closure"
                        helpReference="HandleNotifications"
                        helpText=""
                        helpLink=""
                        fieldName="nNotificationClose"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationClose}
                        onChange={(event) => {
                          setFieldValue("nNotificationClose", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nNotificationProtection */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Notifications for monitoring events"
                        helpReference="HandleNotifications"
                        helpText=""
                        helpLink=""
                        fieldName="nNotificationProtection"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nNotificationProtection}
                        onChange={(event) => {
                          setFieldValue("nNotificationProtection", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
